import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const STORAGE_KEY = 'mnf_site_lang';
const FALLBACK_LANG = 'en';

const translations = {
  en: {
    languageLabel: 'Language:',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    support: 'Support',
    copyright: 'Copyright © 2026 My Nature Friends',
    supportContact: 'Please contact us at :',
    backToHome: 'Back to home',
    supportPageTitle: 'My Nature Friends | Support',
    homeIntroTitle: 'About My Nature Friends',
    homeIntro:
      'My Nature Friends is an independent creative team that creates original picture books inspired by nature, such as Dodo Stories and Journey of Water. Through simple and warm stories, we share the connection between people and the natural world, hoping children can feel nature and gradually learn to feel the world through reading.',
  },
  es: {
    languageLabel: 'Idioma:',
    privacyPolicy: 'Política de Privacidad',
    termsOfService: 'Términos de Servicio',
    support: 'Soporte',
    copyright: 'Derechos de autor © 2026 My Nature Friends',
    supportContact: 'Por favor contáctenos en :',
    backToHome: 'Volver al inicio',
    supportPageTitle: 'My Nature Friends | Soporte',
    homeIntroTitle: 'Sobre My Nature Friends',
    homeIntro:
      'My Nature Friends es un equipo creativo independiente que crea libros ilustrados originales inspirados en la naturaleza, como Historias de Dodo y El viaje del agua. A través de historias simples y cálidas, contamos la conexión entre las personas y el mundo natural, con la esperanza de que los niños puedan sentir la naturaleza y descubrir el mundo a través de la lectura.',
  },
  fi: {
    languageLabel: 'Kieli:',
    privacyPolicy: 'Tietosuojakäytäntö',
    termsOfService: 'Käyttöehdot',
    support: 'Tuki',
    copyright: 'Tekijänoikeus © 2026 My Nature Friends',
    supportContact: 'Ota meihin yhteyttä :',
    backToHome: 'Takaisin etusivulle',
    supportPageTitle: 'My Nature Friends | Tuki',
    homeIntroTitle: 'My Nature Friends -tiimista',
    homeIntro:
      'My Nature Friends on itsenäinen luova tiimi, joka tekee luontoaiheisia alkuperäisiä kuvakirjoja, kuten Dodon tarina ja Veden matka. Tarinoissamme kerrotaan ihmisen ja luonnon yhteydestä. Toivomme, että lapset voivat lukemisen kautta aistia luontoa ja vähitellen oppia tuntemaan maailmaa.',
  },
  zhs: {
    languageLabel: '语言：',
    privacyPolicy: '隐私政策',
    termsOfService: '服务条款',
    support: '支持',
    copyright: '版权所有 © 2026 My Nature Friends',
    supportContact: '请通过以下方式联系我们：',
    backToHome: '返回首页',
    supportPageTitle: 'My Nature Friends | 支持',
    homeIntroTitle: '关于 My Nature Friends',
    homeIntro:
      'My Nature Friends 是一个原创创作团队，创作以自然为主题的原创绘本，如《渡渡绘本》《水之旅》等。我们通过简单而温暖的故事，讲述人与大自然万物之间的联系，希望孩子在阅读中感受自然，也慢慢学会感受世界。',
  },
  zht: {
    languageLabel: '語言：',
    privacyPolicy: '隱私政策',
    termsOfService: '服務條款',
    support: '支援',
    copyright: '版權所有 © 2026 My Nature Friends',
    supportContact: '請透過以下方式聯絡我們：',
    backToHome: '返回首頁',
    supportPageTitle: 'My Nature Friends | 支援',
    homeIntroTitle: '關於 My Nature Friends',
    homeIntro:
      'My Nature Friends 是一個原創創作團隊，創作以自然為主題的原創繪本，如《渡渡繪本》《水之旅》等。我們透過簡單而溫暖的故事，講述人與大自然萬物之間的連結，希望孩子在閱讀中感受自然，也慢慢學會感受世界。',
  },
};

const languageOptions = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fi', label: 'Suomi' },
  { value: 'zhs', label: '简体中文' },
  { value: 'zht', label: '繁體中文' },
];

function normalizeLanguage(lang) {
  const value = (lang || '').toLowerCase();
  if (value.startsWith('zh-cn') || value.startsWith('zh-sg') || value === 'zh-hans' || value.startsWith('zh-hans')) {
    return 'zhs';
  }
  if (value.startsWith('zh-tw') || value.startsWith('zh-hk') || value.startsWith('zh-mo') || value === 'zh-hant' || value.startsWith('zh-hant')) {
    return 'zht';
  }
  if (value.startsWith('zh')) {
    return 'zhs';
  }
  if (value.startsWith('es')) {
    return 'es';
  }
  if (value.startsWith('fi')) {
    return 'fi';
  }
  return 'en';
}

function getInitialLanguage() {
  try {
    const siteLang = localStorage.getItem(STORAGE_KEY);
    if (translations[siteLang]) {
      return siteLang;
    }

    const privacyLang = localStorage.getItem('mnf_privacy_lang');
    if (translations[privacyLang]) {
      return privacyLang;
    }

    const termsLang = localStorage.getItem('mnf_terms_lang');
    if (translations[termsLang]) {
      return termsLang;
    }
  } catch (error) {
    // no-op
  }

  const browserLang = typeof navigator !== 'undefined' ? navigator.language || navigator.userLanguage : FALLBACK_LANG;
  return normalizeLanguage(browserLang);
}

const SiteI18nContext = createContext({
  lang: FALLBACK_LANG,
  setLang: () => {},
  t: (key) => key,
  languageOptions,
});

export function SiteI18nProvider({ children }) {
  const [lang, setLangState] = useState(getInitialLanguage);

  const setLang = useCallback((nextLang) => {
    if (!translations[nextLang]) {
      return;
    }

    setLangState(nextLang);
    try {
      localStorage.setItem(STORAGE_KEY, nextLang);
    } catch (error) {
      // no-op
    }
  }, []);

  const t = useCallback(
    (key) => {
      const localeTable = translations[lang] || translations[FALLBACK_LANG];
      return localeTable[key] || translations[FALLBACK_LANG][key] || key;
    },
    [lang]
  );

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t,
      languageOptions,
    }),
    [lang, setLang, t]
  );

  return <SiteI18nContext.Provider value={value}>{children}</SiteI18nContext.Provider>;
}

export function useSiteI18n() {
  return useContext(SiteI18nContext);
}
